import { useServicesContext } from "@modules/core/services/services.context"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export const useProgramBundleSubscribers = (programBundleId: string) => {
  const { programBundleSubscriberApi } = useServicesContext()

  const { data, isLoading, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: ["programBundleSubscribers", programBundleId],
    queryFn: ({ pageParam }) => {
      return programBundleSubscriberApi.getProgramBundleSubscribers(programBundleId, pageParam, 40)
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const subscriberList = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.content)
        .map((content) => ProgramBundleSubscriberEntity.fromItemDto(content)) ?? [],
    [data],
  )

  return {
    data: {
      ...data,
      content: subscriberList,
      totalSize: data?.pages[0]?.totalSize ?? 0,
    },
    isLoading,
    refetch,
    fetchNextPage,
  }
}

export const useProgramBundleSubscribersSearch = (programBundleId: string, searchTerm?: string) => {
  const { programBundleSubscriberApi } = useServicesContext()

  const { data, isLoading, refetch, isFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: ["programBundleSubscribersSearch", programBundleId, searchTerm?.toLocaleLowerCase()],
    queryFn: ({ pageParam }) => {
      return programBundleSubscriberApi.searchProgramBundleSubscribers(
        programBundleId,
        pageParam,
        40,
        searchTerm?.toLocaleLowerCase(),
      )
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const subscriberList = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.content)
        .map((content) => ProgramBundleSubscriberEntity.fromItemDto(content)) ?? [],
    [data],
  )

  return {
    data: {
      ...data,
      content: subscriberList,
      totalSize: data?.pages[0].totalSize ?? 0,
    },
    isLoading,
    refetch,
    isFetching,
    fetchNextPage,
  }
}
